import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'
import { isCaseId, isReceiptId, resolveStaffDocument } from '../../services/caseDocumentService'

/**
 * CaseDocuments — full-fidelity port matching Scale's Documents/Documents.jsx.
 *
 * Sections:
 *   1. Case Documents (Service List, Caption Page / Operative Complaint,
 *      Intake Form (CAR), RRM Form (CAR))
 *   2. Plaintiff Documents: {name} (with "Mediation Statement Received Manually" toggle)
 *   2a. {Plaintiff Position} Documents: {name} — one per additional party
 *   3. Defendant Documents: {name} (with toggle)
 *   3a. {Defendant Position} Documents: {name} — one per additional party
 *
 * Each file row shows: filename + category label + submission date + View link.
 */

/* Largest file the Upload button can actually send.
 *
 * The file is base64-encoded into a JSON body, which inflates it by a third,
 * and casemanager-be caps that body at 50mb (server.js). So the real ceiling
 * is 50 x 3/4 = 37.5MB; 37 leaves room for the JSON field names and filename.
 * Keep this in step with the backend limit — if they drift, the user is told
 * one number and rejected at another. */
const MAX_UPLOAD_MB = 37
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024
const formatMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1)

function fmtShortDate(s) {
    if (!s) return null
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return null
    // "May 04, 2026" — keep consistent with the rest of the case detail page.
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })
}

const DOCUMENT_FAILED = 'This document could not be opened. Please try again.'
const POPUP_BLOCKED = 'Your browser blocked the document tab. Allow pop-ups for this site and try again.'

// Opens private recipient documents. Shared through context so every DocList
// (case, parties, additional parties) uses the same case id, toasts and
// in-flight tracking.
const ReceiptOpenerContext = createContext(null)

// A signed link is requested on every click and never stored. The tab is opened
// synchronously inside the click (so pop-up blockers allow it) and only then
// pointed at the link, so this page never navigates whatever the storage
// response does. Any failure closes the tab and shows one generic message;
// there is never a fallback to a stored URL.
// The user may already have closed the tab, or the browser may refuse; either
// way there is nothing left to clean up.
function closeTab(tab) {
    try { if (tab && !tab.closed) tab.close() } catch { /* already gone */ }
}

function useReceiptOpener(caseId, onToast) {
    // lowercased receipt id -> { ctrl, tab }
    const inFlight = useRef(new Map())
    const [pending, setPending] = useState(() => new Set())

    // When the case changes or the page unmounts, abort every request and close
    // its blank tab NOW, so a request that never settles cannot leave a tab
    // behind. Later callbacks for these entries do nothing.
    useEffect(() => {
        const requests = inFlight.current
        return () => {
            requests.forEach(({ ctrl, tab }) => { ctrl.abort(); closeTab(tab) })
            requests.clear()
            setPending(new Set())
        }
    }, [caseId])

    const open = useCallback((receiptId) => {
        if (!isCaseId(caseId) || !isReceiptId(receiptId)) return
        const key = receiptId.toLowerCase()
        if (inFlight.current.has(key)) return
        let tab = null
        try { tab = window.open('', '_blank') } catch { tab = null }
        if (!tab) {
            onToast?.({ type: 'error', message: POPUP_BLOCKED })
            return
        }
        try { tab.opener = null } catch { /* already detached */ }
        const entry = { ctrl: new AbortController(), tab }
        inFlight.current.set(key, entry)
        setPending((s) => new Set(s).add(key))
        const live = () => inFlight.current.get(key) === entry && !entry.ctrl.signal.aborted
        resolveStaffDocument({ caseId, receiptId: key, signal: entry.ctrl.signal })
            .then(({ url }) => {
                if (!live()) return
                // Closed by the user while we waited: nothing to show, nothing to report.
                if (tab.closed) return
                tab.location.replace(url)
            })
            .catch(() => {
                if (!live()) return
                closeTab(tab)
                onToast?.({ type: 'error', message: DOCUMENT_FAILED })
            })
            .finally(() => {
                if (inFlight.current.get(key) !== entry) return
                inFlight.current.delete(key)
                setPending((s) => { const n = new Set(s); n.delete(key); return n })
            })
    }, [caseId, onToast])

    return useMemo(() => ({ open, pending, caseOk: isCaseId(caseId) }), [open, pending, caseId])
}

function FileRow({ file }) {
    const opener = useContext(ReceiptOpenerContext)
    const isReceipt = file.receipt === true
    const busy = isReceipt && file.receiptValid && opener?.pending.has(file.receiptId.toLowerCase())
    const enabled = isReceipt ? (file.receiptValid && opener?.caseOk === true && !busy) : !!file.url
    const open = () => {
        if (isReceipt) {
            if (enabled) opener.open(file.receiptId)
            return
        }
        if (file.url) window.open(file.url, '_blank', 'noopener,noreferrer')
    }
    return (
        <div
            role={enabled ? 'button' : undefined}
            tabIndex={enabled ? 0 : -1}
            aria-disabled={enabled ? undefined : true}
            aria-busy={busy ? true : undefined}
            onClick={open}
            onKeyDown={(e) => {
                if (enabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    open()
                }
            }}
            className={`border border-gray-200 rounded-md p-2 flex items-center gap-2 hover:bg-gray-100 transition-colors ${enabled ? 'cursor-pointer' : busy ? 'opacity-60 cursor-wait' : 'opacity-60 cursor-not-allowed'}`}
        >
            <svg className="w-9 h-10 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2v6h6" />
            </svg>
            <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 truncate">{file.name || 'Document'}</h2>
                {file.category && (
                    <span className="inline-block rounded-md bg-teal-800 text-white text-[10px] font-medium px-1.5 py-0.5 mt-0.5">
                        {file.category}
                    </span>
                )}
                {file.uploaded_at && (
                    <p className="text-xs text-gray-500 mt-0.5">submitted on {fmtShortDate(file.uploaded_at)}</p>
                )}
            </div>
        </div>
    )
}

function DocList({ docs }) {
    if (!docs.length) return <p className="text-sm text-gray-500 py-1">No documents available.</p>
    return (
        <div className="flex items-center flex-wrap gap-3">
            {docs.map((d, i) => <FileRow key={d.id || i} file={d} />)}
        </div>
    )
}

function ManualReceivedToggle({ received, onChange, busy, briefLabel = 'Brief' }) {
    // When the brief has already been received (manually flagged or
    // uploaded), render a green confirmation pill instead of a checkbox
    // — the toggle is gone so users can't accidentally un-mark it from
    // here, matching the Scale-portal behavior.
    if (received) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.5l2.5 2.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {briefLabel} received
            </span>
        )
    }
    return (
        <label className={`flex items-center gap-2 text-xs ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}>
            <input
                type="checkbox"
                checked={false}
                disabled={busy}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
            />
            <span className="text-gray-700">{briefLabel} Received Manually</span>
        </label>
    )
}

function UploadModal({ isOpen, onClose, targets, defaultKey, caseId, onUploaded, onToast }) {
    const [selectedKey, setSelectedKey] = useState(defaultKey || 'case')
    const [docType, setDocType] = useState('mediation_statement')
    const [file, setFile] = useState(null)
    const [busy, setBusy] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const inputRef = useRef(null)

    // Reset target/type/file each time the modal (re)opens so it starts clean
    // and honors whichever party the caller pre-selected.
    useEffect(() => {
        if (isOpen) {
            setSelectedKey(defaultKey || 'case')
            setDocType('mediation_statement')
            setFile(null)
        }
    }, [isOpen, defaultKey])

    if (!isOpen) return null

    const target = targets.find((t) => t.key === selectedKey) || targets[0]

    const handleUpload = async () => {
        if (!file) {
            onToast?.({ type: 'error', message: 'Pick a file first' })
            return
        }
        // Reject oversized files here rather than letting Express refuse the
        // parsed body — that surfaces a bare "request entity too large" with
        // no mention of a limit, which is what Case Manager Services hit on
        // the Holmberg briefs. Checked before the FileReader runs so a large
        // file is never read into memory just to be thrown away.
        if (file.size > MAX_UPLOAD_BYTES) {
            onToast?.({
                type: 'error',
                message: `This file is ${formatMb(file.size)} MB. The maximum upload size is ${MAX_UPLOAD_MB} MB — please compress or split it and try again.`,
            })
            return
        }
        try {
            setBusy(true)
            // Convert via FileReader instead of String.fromCharCode on a
            // huge spread — large PDFs blow the call stack with the
            // previous approach (RangeError: Maximum call stack size).
            const b64 = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => {
                    const result = String(reader.result || '')
                    const comma = result.indexOf(',')
                    resolve(comma >= 0 ? result.slice(comma + 1) : result)
                }
                reader.onerror = () => reject(reader.error)
                reader.readAsDataURL(file)
            })
            await api.post(`/cases/${caseId}/documents/upload`, {
                party_type: target?.party_type || 'case',
                document_type: docType,
                file_name: file.name,
                file_base64: b64,
                content_type: file.type,
                client_id: target?.client_id || null,
            })
            onToast?.({ type: 'success', message: 'Document uploaded' })
            if (onUploaded) await onUploaded()
            onClose()
        } catch (err) {
            // Surface the BE error message instead of a generic "Upload failed"
            // so users can see the actual problem (e.g., file too large,
            // bucket missing, base64 invalid).
            const msg = err?.response?.data?.message || err?.message || 'Upload failed'
            onToast?.({ type: 'error', message: msg })
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !busy && onClose()}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <h5 className="text-lg font-semibold mb-4">Upload Document</h5>
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Client</label>
                        <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                            {targets.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Document Type</label>
                        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                            <option value="mediation_statement">Mediation Statement</option>
                            <option value="additional">Additional Document</option>
                            <option value="service_list">Service List</option>
                            <option value="caption_page">Caption Page</option>
                            <option value="court_order">Court Order</option>
                            <option value="intake_form">Intake Form</option>
                            <option value="rrm_form">RRM Form</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">File</label>
                        <div
                            onClick={() => inputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f) }}
                            className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400'}`}
                        >
                            <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                            </svg>
                            {file ? (
                                <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-600">Drop your file here, or <span className="text-teal-600 font-medium">browse</span></p>
                                    <p className="text-xs text-gray-400 mt-1">Support: PDF, JPEG, JPG, PNG, XLSX, XLS, CSV, DOC, and DOCX files are allowed.</p>
                                </>
                            )}
                            <input ref={inputRef} type="file" className="hidden" accept=".pdf,.jpeg,.jpg,.png,.xlsx,.xls,.csv,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">Cancel</button>
                    <button type="button" onClick={handleUpload} disabled={busy || !file} className="px-4 py-2 text-sm bg-[#173336] text-white rounded-md disabled:opacity-50">
                        {busy ? 'Uploading…' : 'Upload'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Defensive array helper: returns an empty array if x isn't a real array
// (e.g. when a JSONB column holds an object/string instead of a list).
const safeArr = (x) => (Array.isArray(x) ? x : [])

// Extract a readable filename from a Supabase Storage URL.
// e.g. ".../uploads/1754673597014_commissions_2024-07-10.csv" → "commissions_2024-07-10.csv"
function extractFileName(url) {
    if (!url || typeof url !== 'string') return 'Document'
    try {
        const u = new URL(url)
        const last = decodeURIComponent(u.pathname.split('/').pop() || '')
        // Strip leading timestamp prefix (e.g., "1754673597014_")
        const stripped = last.replace(/^\d{10,}_/, '')
        return stripped || last || 'Document'
    } catch {
        return url.split('/').pop()?.replace(/^\d{10,}_/, '') || 'Document'
    }
}

// A private recipient upload is stored as { receipt_id, ... } with no usable
// URL. The PRESENCE of receipt_id decides: whatever its value, the entry is a
// receipt, any url on it is ignored, and a malformed id leaves the row
// disabled. It never falls back to a stored URL.
const hasReceiptField = (d) => !!d && typeof d === 'object' && Object.prototype.hasOwnProperty.call(d, 'receipt_id')
const textOr = (v, fallback) => (typeof v === 'string' && v.trim() ? v : fallback)

function receiptRow(d, fallbackUploadedAt, defaultName, category) {
    const valid = isReceiptId(d.receipt_id)
    return {
        id: valid ? d.receipt_id : undefined,
        receipt: true,
        receiptId: valid ? d.receipt_id : null,
        receiptValid: valid,
        url: null,
        name: textOr(d.name, textOr(d.file_name, textOr(d.fileName, defaultName))),
        uploaded_at: d.created_at || d.uploaded_at || fallbackUploadedAt,
        category,
    }
}

const isListed = (norm) => !!norm && (!!norm.url || norm.receipt === true)

// Normalize a doc entry into { name, url, uploaded_at }. The DB stores docs
// as plain URL strings inside the JSONB array, BUT some rows store objects
// with { url, name, created_at }. Tolerate both.
function normalizeDoc(d, fallbackUploadedAt, defaultName, category) {
    if (!d) return null
    if (typeof d === 'string') {
        return { url: d, name: extractFileName(d) || defaultName, uploaded_at: fallbackUploadedAt, category }
    }
    if (hasReceiptField(d)) return receiptRow(d, fallbackUploadedAt, defaultName, category)
    if (typeof d === 'object') {
        const url = d.url || d.file_url || d.fileUrl || null
        return {
            id: d.id,
            url,
            name: d.name || d.file_name || d.fileName || (url ? extractFileName(url) : defaultName),
            uploaded_at: d.created_at || d.uploaded_at || fallbackUploadedAt,
            category,
        }
    }
    return null
}

function pushAll(target, source, fallbackDate, defaultName, category) {
    safeArr(source).forEach((d) => {
        const norm = normalizeDoc(d, fallbackDate, defaultName, category)
        if (isListed(norm)) target.push(norm)
    })
}

function partyDocs(party, briefLabel = 'Brief') {
    if (!party) return []
    const onb = party.onboarding || {}
    const meta = party.odrCaseMetaData || {}
    const onbDate = onb.created_at || null
    const metaDate = meta.created_at || null
    const docs = []

    pushAll(docs, onb.brief_info, onbDate, briefLabel, briefLabel)
    // The legacy brief_url never stands in for a receipt brief, even a malformed one.
    const briefHasReceipt = safeArr(onb.brief_info).some(hasReceiptField)
    if (!briefHasReceipt && !docs.some((d) => d.category === briefLabel) && onb.brief_url) {
        docs.push({ url: onb.brief_url, name: extractFileName(onb.brief_url) || briefLabel, uploaded_at: onbDate, category: briefLabel })
    }
    pushAll(docs, onb.additional_docs, onbDate, 'Additional Document', 'Additional Document')
    pushAll(docs, onb.mediator_docs, onbDate, 'Mediator Document', 'Mediator Documents')
    pushAll(docs, onb.mediator_statement_docs, onbDate, 'Mediation Statement', 'Mediation Statement')
    pushAll(docs, onb.court_adr_order_docs, onbDate, 'Court ADR Order', 'Court ADR Order')

    pushAll(docs, meta.additional_docs, metaDate, 'Additional Document', 'Additional Document')
    pushAll(docs, meta.court_adr_order_docs, metaDate, 'Court ADR Order', 'Court ADR Order')
    pushAll(docs, meta.mediator_statement_docs, metaDate, 'Mediation Statement', 'Mediation Statement')
    pushAll(docs, meta.mediator_docs, metaDate, 'Mediator Document', 'Mediator Documents')

    safeArr(party.documents).forEach((d) => {
        const norm = normalizeDoc(d, onbDate, d.type || 'Document', d.type || 'Document')
        if (isListed(norm)) docs.push(norm)
    })
    return docs
}

export default function CaseDocuments({ onToast, onRefresh }) {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel } = useContext(CaseDetailContext)
    const [uploadFor, setUploadFor] = useState(null)
    const [busyParty, setBusyParty] = useState(null)
    // Scale defaults to "Brief"; mediator-specific brief_text overrides.
    const briefLabel = caseData?.mediatorSettings?.brief_text || 'Brief'
    const receiptOpener = useReceiptOpener(caseData?.id, onToast)

    const caseDocs = useMemo(() => {
        const docs = []
        const meta = caseData?.casemetadata || caseData?.metadata || {}
        const caseCreated = caseData?.created_at || null

        // The DB stores `service_list` and `caption_page` as either:
        //   - a plain URL string (single file)
        //   - a stringified empty array "[]"
        //   - a JSONB array of URL strings
        //   - an array of objects {url,name,created_at}
        // Normalize all of these into { url, name, category, uploaded_at }.
        const collectFromField = (value, defaultName, category) => {
            if (!value) return
            // Stringified empty array — treat as nothing
            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (trimmed === '' || trimmed === '[]' || trimmed === 'null') return
                // Could be a JSON array stringified — try to parse it.
                if (trimmed.startsWith('[')) {
                    try {
                        const parsed = JSON.parse(trimmed)
                        if (Array.isArray(parsed)) {
                            parsed.forEach((p) => {
                                const norm = normalizeDoc(p, caseCreated, defaultName, category)
                                if (isListed(norm)) docs.push(norm)
                            })
                            return
                        }
                    } catch { /* fall through, treat as URL */ }
                }
                // Plain URL string
                docs.push({ url: trimmed, name: extractFileName(trimmed) || defaultName, uploaded_at: caseCreated, category })
                return
            }
            // Real array
            safeArr(value).forEach((d) => {
                const norm = normalizeDoc(d, caseCreated, defaultName, category)
                if (isListed(norm)) docs.push(norm)
            })
        }

        collectFromField(caseData?.service_list, 'Service List', 'Service List')
        if (!docs.some((d) => d.category === 'Service List') && meta.service_list_url) {
            docs.push({ url: meta.service_list_url, name: extractFileName(meta.service_list_url) || 'Service List', category: 'Service List', uploaded_at: caseCreated })
        }
        collectFromField(caseData?.caption_page, 'Caption Page', 'Operative Complaint')
        if (!docs.some((d) => d.category === 'Operative Complaint') && meta.caption_page_url) {
            docs.push({ url: meta.caption_page_url, name: extractFileName(meta.caption_page_url) || 'Caption Page', category: 'Operative Complaint', uploaded_at: caseCreated })
        }

        // CAR forms (when present — column may not exist on every env)
        safeArr(caseData?.car_documents).forEach((d) => {
            const t = (d.document_type || d.type || '').toLowerCase()
            const isIntake = t.includes('intake')
            const isRrm = t.includes('rrm')
            if (!isIntake && !isRrm) return
            const cat = isIntake ? 'Intake Form' : 'RRM Form'
            if (hasReceiptField(d)) { docs.push(receiptRow(d, caseCreated, cat, cat)); return }
            const url = d.file_url || d.url
            if (!url) return
            docs.push({ id: d.id, url, name: d.file_name || extractFileName(url) || cat, category: cat, uploaded_at: d.created_at || caseCreated })
        })

        // Case-level documents uploaded via the Upload modal. The BE stores them
        // in cases.casemetadata.documents and getCase surfaces them as
        // caseData.caseDocuments. Dedupe by URL so a service_list/caption_page
        // upload already added via its *_url fallback above isn't shown twice.
        safeArr(caseData?.caseDocuments).forEach((d) => {
            if (hasReceiptField(d)) {
                const dt = (d.document_type || '').toLowerCase()
                const category = dt === 'service_list' ? 'Service List'
                    : dt === 'caption_page' ? 'Operative Complaint'
                    : 'Case Document'
                docs.push(receiptRow(d, caseCreated, 'Document', category))
                return
            }
            const url = d.url || d.file_url
            if (!url || docs.some((x) => x.url === url)) return
            const dt = (d.document_type || '').toLowerCase()
            const category = dt === 'service_list' ? 'Service List'
                : dt === 'caption_page' ? 'Operative Complaint'
                : 'Case Document'
            docs.push({ id: d.id || url, url, name: d.name || extractFileName(url) || 'Document', category, uploaded_at: d.created_at || caseCreated })
        })
        return docs
    }, [caseData])

    const setManualBriefReceived = async (party, value) => {
        const cid = party?.client_id || party?.clientId
        if (!cid || !caseData?.id) return
        try {
            setBusyParty(cid)
            await api.post(`/cases/${caseData.id}/brief-manual`, {
                client_id: cid,
                value,
            })
            onToast?.({ type: 'success', message: value ? `${briefLabel} marked received` : `${briefLabel} marked not received` })
            // Wait for the parent to refetch so the brief dot turns green
            // (or revert to red/grey) before this row re-renders.
            if (onRefresh) await onRefresh()
        } catch (err) {
            onToast?.({ type: 'error', message: err?.response?.data?.message || `Failed to update ${briefLabel.toLowerCase()} status` })
        } finally {
            setBusyParty(null)
        }
    }

    // Brief is considered "received" when the manual flag is set OR there
    // are uploaded brief documents. In either case the toggle becomes a
    // read-only confirmation row (matches the user's "shouldn't allow
    // re-toggling once true" expectation).
    const isBriefReceived = (party) => {
        const onb = party?.onboarding || {}
        const hasDocs = Array.isArray(onb.brief_info) && onb.brief_info.length > 0
        return !!onb.is_brief_submit_manually || hasDocs
    }

    const plaintiff = caseData?.plaintiff
    const defendant = caseData?.defendant
    const plaintiffAdditional = plaintiff?.additionalParties || []
    const defendantAdditional = defendant?.additionalParties || []

    // Targets for the upload modal's "Client" selector — case-level plus each
    // party (and additional parties) so a document is filed against the right
    // onboarding row and shows in that party's section, matching the Scale
    // portal's Client dropdown. party_type + client_id map straight to what
    // the BE's uploadCaseDocument handler expects.
    const uploadTargets = useMemo(() => {
        const t = [{ key: 'case', label: 'Case (both parties)', party_type: 'case', client_id: null }]
        if (plaintiff?.client_id) {
            t.push({ key: 'plaintiff', label: `${plaintiffPartyLabel}${plaintiff.name ? `: ${plaintiff.name}` : ''}`, party_type: 'plaintiff', client_id: plaintiff.client_id })
        }
        plaintiffAdditional.forEach((p, i) => {
            const cid = p.client_id || p.clientId || p.client?.client_id
            if (cid) t.push({ key: `p-add-${i}`, label: `${plaintiffPartyLabel} ${p.position || 'Additional'}${(p.client?.name || p.name) ? `: ${p.client?.name || p.name}` : ''}`, party_type: 'plaintiff', client_id: cid })
        })
        if (defendant?.client_id) {
            t.push({ key: 'defendant', label: `${defendantPartyLabel}${defendant.name ? `: ${defendant.name}` : ''}`, party_type: 'defendant', client_id: defendant.client_id })
        }
        defendantAdditional.forEach((p, i) => {
            const cid = p.client_id || p.clientId || p.client?.client_id
            if (cid) t.push({ key: `d-add-${i}`, label: `${defendantPartyLabel} ${p.position || 'Additional'}${(p.client?.name || p.name) ? `: ${p.client?.name || p.name}` : ''}`, party_type: 'defendant', client_id: cid })
        })
        return t
    }, [plaintiff, defendant, plaintiffAdditional, defendantAdditional, plaintiffPartyLabel, defendantPartyLabel])

    return (
        <ReceiptOpenerContext.Provider value={receiptOpener}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-5 md:px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
                <div>
                    <div className="text-base font-semibold text-gray-900">Case Documents</div>
                    <p className="text-xs text-gray-500">Submitted by both parties during onboarding form</p>
                </div>
                <button
                    type="button"
                    onClick={() => setUploadFor('case')}
                    className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex items-center gap-1"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Upload
                </button>
            </div>

            <div className="px-5 md:px-6 py-4 border-b border-gray-100">
                <div className="text-sm font-semibold text-gray-900 mb-2">Case Documents</div>
                <DocList docs={caseDocs} />
            </div>

            {plaintiff?.client_id && (
                <div className="px-5 md:px-6 py-4 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="text-sm font-semibold text-gray-900">{plaintiffPartyLabel} Documents{plaintiff.name ? `: ${plaintiff.name}` : ''}</div>
                        <ManualReceivedToggle
                            briefLabel={briefLabel}
                            busy={busyParty === plaintiff.client_id}
                            received={isBriefReceived(plaintiff)}
                            onChange={(v) => setManualBriefReceived(plaintiff, v)}
                        />
                    </div>
                    <DocList docs={partyDocs(plaintiff, briefLabel)} />
                </div>
            )}

            {plaintiffAdditional.map((p, i) => (
                <div key={`p-add-${i}`} className="px-5 md:px-6 py-4 border-b border-gray-100">
                    <div className="text-sm font-semibold text-gray-900 mb-2">
                        {plaintiffPartyLabel} {p.position || 'Additional'} Documents{(p.client?.name || p.name) ? `: ${p.client?.name || p.name}` : ''}
                    </div>
                    <DocList docs={partyDocs(p, briefLabel)} />
                </div>
            ))}

            {defendant?.client_id && (
                <div className="px-5 md:px-6 py-4 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="text-sm font-semibold text-gray-900">{defendantPartyLabel} Documents{defendant.name ? `: ${defendant.name}` : ''}</div>
                        <ManualReceivedToggle
                            briefLabel={briefLabel}
                            busy={busyParty === defendant.client_id}
                            received={isBriefReceived(defendant)}
                            onChange={(v) => setManualBriefReceived(defendant, v)}
                        />
                    </div>
                    <DocList docs={partyDocs(defendant, briefLabel)} />
                </div>
            )}

            {defendantAdditional.map((p, i) => (
                <div key={`d-add-${i}`} className="px-5 md:px-6 py-4 border-b border-gray-100 last:border-b-0">
                    <div className="text-sm font-semibold text-gray-900 mb-2">
                        {defendantPartyLabel} {p.position || 'Additional'} Documents{(p.client?.name || p.name) ? `: ${p.client?.name || p.name}` : ''}
                    </div>
                    <DocList docs={partyDocs(p, briefLabel)} />
                </div>
            ))}

            <UploadModal
                isOpen={!!uploadFor}
                onClose={() => setUploadFor(null)}
                targets={uploadTargets}
                defaultKey={typeof uploadFor === 'string' ? uploadFor : 'case'}
                caseId={caseData?.id}
                onToast={onToast}
                onUploaded={onRefresh}
            />
        </div>
        </ReceiptOpenerContext.Provider>
    )
}
